import React, { useState, useEffect, useContext } from "react";
import { Modal, Input } from "antd";
import { useMutation } from "react-query";
import client from "src/api";
import { UpdateUserPhoneResponse } from "src/api/types";
import GlobalContext from "src/context/global-context";
import { PhoneVerificationIcon } from "src/components/icons/phone-verification-icon";
import { SpinnerIcon } from "src/components/icons/spinner-icon";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
} from "src/lib/constants";
import { getErrorMessage } from "src/lib/utils";

interface EditPhoneModalProps {
  open: boolean;
  onClose: () => void;
  currentPhone: string;
  userId: number | string;
  isBusiness?: boolean;
  onSuccess: (newPhone: string) => void;
}

export const EditPhoneModal: React.FC<EditPhoneModalProps> = ({
  open,
  onClose,
  currentPhone,
  userId,
  isBusiness = false,
  onSuccess,
}) => {
  const { setIsAlertOpened, setAlertText, setAlertTitle } =
    useContext(GlobalContext);

  const [phoneInput, setPhoneInput] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Normalize phone for comparison or display
  const normalizePhone = (phoneStr: string) => {
    if (!phoneStr) return "";
    let trimmed = phoneStr.trim().replace(/[^\d+]/g, "");
    if (trimmed && !trimmed.startsWith("+")) {
      trimmed = `+${trimmed}`;
    }
    return trimmed;
  };

  useEffect(() => {
    if (open) {
      const normalized = normalizePhone(currentPhone);
      setPhoneInput(normalized);
      setError("");
    }
  }, [open, currentPhone]);

  const { mutate: updatePhone, isLoading } = useMutation<
    UpdateUserPhoneResponse,
    any,
    { id: number | string; phone: string }
  >(
    isBusiness
      ? client.users.updateBusinessUserPhone
      : client.users.updateIndividualUserPhone,
    {
      onSuccess: (response) => {
        const updatedPhone =
          response?.data?.phone || normalizePhone(phoneInput);
        onSuccess(updatedPhone);

        if (setAlertTitle) {
          setAlertTitle("Success");
        }
        setAlertText(response?.message || "USER_UPDATE_SUCCESS");
        setIsAlertOpened(true);
        handleClose();
      },
      onError: (err: any) => {
        let msg = APISomethingWrongMsg;
        if (err?.code === "ERR_NETWORK") {
          msg = NetworkDisconnectMsg;
        } else if (err?.response?.data) {
          msg = getErrorMessage(err.response.data);
        } else if (err?.message) {
          msg = err.message;
        }
        setError(msg);
        if (setAlertTitle) {
          setAlertTitle("Error");
        }
        setAlertText(msg);
        setIsAlertOpened(true);
      },
    },
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Allow only digits and leading '+'
    let cleaned = val.replace(/[^\d+]/g, "");
    if (cleaned.includes("+")) {
      cleaned = "+" + cleaned.replace(/\+/g, "");
    }
    setPhoneInput(cleaned);
    if (error) {
      setError("");
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setError("");
    onClose();
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLoading) return;

    let formatted = normalizePhone(phoneInput);

    if (!formatted) {
      setError("Phone number is required");
      return;
    }

    // E.164 phone format validation: + followed by 7 to 15 digits (country code 1-9)
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    if (!phoneRegex.test(formatted)) {
      setError(
        "Invalid phone format. Please enter a valid international number (e.g. +995555788848)",
      );
      return;
    }

    const currentNormalized = normalizePhone(currentPhone);
    if (formatted === currentNormalized) {
      setError("Phone number is already set to this value");
      return;
    }

    setError("");
    updatePhone({
      id: userId,
      phone: formatted,
    });
  };

  const isChanged =
    Boolean(phoneInput.trim()) &&
    normalizePhone(phoneInput) !== normalizePhone(currentPhone);

  return (
    <Modal
      title={
        <span className="font-manrope text-[18px] font-semibold text-primary">
          {isBusiness
            ? "Edit Business Phone Number"
            : "Edit Individual Phone Number"}
        </span>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={!isLoading}
      maskClosable={!isLoading}
      centered
      width={460}
      className="custom-phone-modal"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5 pt-4">
        <div>
          <label className="block text-[14px] font-manrope font-medium text-secondary mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <Input
            size="large"
            value={phoneInput}
            onChange={handleInputChange}
            placeholder="+995555788848"
            disabled={isLoading}
            prefix={
              <PhoneVerificationIcon className="w-4 h-4 text-secondary mr-1.5" />
            }
            status={error ? "error" : ""}
            className="rounded-[10px] font-manrope text-[15px]"
            autoFocus
          />
          <p className="mt-1.5 text-[12px] font-manrope text-secondary">
            Format: +[Country Code][Number], e.g. +995555788848
          </p>
          {error && (
            <p className="mt-1.5 text-[13px] font-manrope text-[#F44336]">
              * {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100 dark:border-dark-600">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 rounded-[8px] font-manrope text-[14px] font-medium border border-gray-300 dark:border-dark-600 text-secondary hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !isChanged}
            className={`px-5 py-2 rounded-[8px] font-manrope text-[14px] font-medium text-white flex items-center gap-2 transition-all ${
              isLoading || !isChanged
                ? "bg-gray-400 cursor-not-allowed opacity-70"
                : "bg-primary hover:opacity-90 cursor-pointer shadow-sm"
            }`}
          >
            {isLoading && <SpinnerIcon className="w-4 h-4 animate-spin" />}
            <span>{isLoading ? "Saving..." : "Save"}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditPhoneModal;
